/**
 * Centralized logging utility
 * Provides consistent logging across the application with support for log levels and categories.
 */
import { createErrorLogger, ErrorType } from './errorHandler';

const internalLogger = createErrorLogger('Logger');

// Log levels
export const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3
};

class Logger {
  constructor() {
    this.sessionId = new Date().toISOString().replace(/[:.]/g, '-');
    this.isLevelEnabled = {
      debug: true,
      info: true,
      warn: true,
      error: true
    };
    this.initialize();
  }

  initialize() {
    try {
      const logEntry = {
        timestamp: new Date().toISOString(),
        level: 'INFO',
        category: 'LOGGER_INIT',
        message: 'Logger initialized',
        data: {
          sessionId: this.sessionId,
          timestamp: new Date().toISOString()
        },
        sessionId: this.sessionId,
        url: window.location ? window.location.href : ''
      };

      this._consoleLog(logEntry);

      internalLogger.info('initialize', `Logger initialized with session ID: ${this.sessionId}`, { sessionId: this.sessionId });
    } catch (error) {
      internalLogger.error('initialize', 'Failed to initialize logger', error, ErrorType.GENERAL);
    }
  }

  /**
   * Log a message with additional context
   * @param {number} level - Log level from LOG_LEVELS
   * @param {string} category - Category/component of the log
   * @param {string} message - The log message
   * @param {object} data - Additional data for the log entry
   */
  log(level, category, message, data = {}) {
    try {
      // Create a log entry
      const logEntry = {
        timestamp: new Date().toISOString(),
        level: this._getLevelName(level),
        category,
        message,
        data,
        sessionId: this.sessionId,
        url: window.location ? window.location.href : ''
      };

      this._consoleLog(logEntry);
    } catch (error) {
      internalLogger.error('log', 'Error writing to log', error, ErrorType.GENERAL);
    }
  }

  /**
   * Output log to console with appropriate styling
   */
  _consoleLog(entry) {
    const styles = {
      [LOG_LEVELS.DEBUG]: 'color: gray',
      [LOG_LEVELS.INFO]: 'color: blue',
      [LOG_LEVELS.WARN]: 'color: orange; font-weight: bold',
      [LOG_LEVELS.ERROR]: 'color: red; font-weight: bold'
    };

    const level = Object.keys(LOG_LEVELS).find(key => LOG_LEVELS[key] === entry.level) || entry.level;

    // eslint-disable-next-line no-console
    console.log(
      `%c[${level}] [${entry.category}] ${entry.message}`,
      styles[entry.level] || '',
      entry.data
    );
  }

  /**
   * Convert level number to name
   * @param {number} level - Log level number
   * @returns {string} Level name
   */
  _getLevelName(level) {
    switch (level) {
      case LOG_LEVELS.DEBUG: return 'DEBUG';
      case LOG_LEVELS.INFO: return 'INFO';
      case LOG_LEVELS.WARN: return 'WARN';
      case LOG_LEVELS.ERROR: return 'ERROR';
      default: return 'INFO';
    }
  }

  /**
   * Debug level log
   */
  debug(category, message, data = {}) {
    if (!this.isLevelEnabled.debug) return;
    this.log(LOG_LEVELS.DEBUG, category, message, data);
  }

  /**
   * Info level log
   */
  info(category, message, data = {}) {
    if (!this.isLevelEnabled.info) return;
    this.log(LOG_LEVELS.INFO, category, message, data);
  }

  /**
   * Warning level log
   */
  warn(category, message, data = {}) {
    if (!this.isLevelEnabled.warn) return;
    this.log(LOG_LEVELS.WARN, category, message, data);
  }

  /**
   * Error level log
   */
  error(category, message, data = {}) {
    if (!this.isLevelEnabled.error) return;
    this.log(LOG_LEVELS.ERROR, category, message, data);
  }

  /**
   * Flush logs to a persistent store - DISABLED
   */
  async flushLogs() {
    // Disk logging disabled
    return;
  }

  /**
   * Get all logs for the current session - DISABLED
   */
  async getAllLogs() {
    // Disk logging disabled
    return [];
  }

  /**
   * Download logs as a file - DISABLED
   */
  async downloadLogs() {
    // Disk logging disabled - create a simple message
    const logContent = JSON.stringify({
      message: 'Disk logging has been disabled',
      timestamp: new Date().toISOString()
    }, null, 2);

    // Create a download link
    const blob = new Blob([logContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'logging_disabled.json';
    document.body.appendChild(a);
    a.click();

    // Clean up
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 0);
  }
}

// Export a singleton instance
export const logger = new Logger();

// Export fileLogger alias for backward compatibility
export const fileLogger = logger;

// Re-export errorHandler functions for backward compatibility
export { createErrorLogger, ErrorType } from './errorHandler';

// Utility function to log version operations
export const logVersionOperation = (operation, data = {}) => {
  logger.info('VERSION', operation, {
    ...data,
    timestamp: new Date().toISOString()
  });
};

export default logger;